import { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, limit } from 'firebase/firestore';
import { Bell, Clock, Package, AlertCircle, CheckCircle2, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  createdAt: any;
  data?: any;
}

export function VendorNotifications({ vendorId }: { vendorId: string }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', vendorId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as Notification[];
      setNotifications(docs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [vendorId]);

  const markAsRead = async (id: string) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { read: true });
    } catch (err) {
      console.error('Failed to mark notification as read', err);
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    for (const n of unread) {
      await markAsRead(n.id);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-neutral-500" />
          <h2 className="text-xl font-medium text-neutral-900 dark:text-white font-serif">Store Notifications</h2>
        </div>
        {notifications.some(n => !n.read) && (
          <button 
            onClick={markAllRead}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
        {notifications.length > 0 ? (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {notifications.map((notification) => (
              <div 
                key={notification.id}
                onClick={() => !notification.read && markAsRead(notification.id)}
                className={`p-4 sm:p-6 transition-colors cursor-pointer group hover:bg-neutral-50 dark:hover:bg-neutral-800/50 ${
                  !notification.read ? 'bg-indigo-50/30 dark:bg-indigo-900/10' : ''
                }`}
              >
                <div className="flex gap-4">
                  <div className={`mt-1 p-2 rounded-lg shrink-0 ${
                    notification.type === 'order_update' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                    notification.type === 'pickup_reminder' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                    'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                  }`}>
                    {notification.type === 'order_update' ? <Package className="w-5 h-5" /> : 
                     notification.type === 'pickup_reminder' ? <Clock className="w-5 h-5" /> : 
                     <AlertCircle className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className={`text-sm font-semibold truncate ${
                        !notification.read ? 'text-neutral-900 dark:text-white' : 'text-neutral-700 dark:text-neutral-300'
                      }`}>
                        {notification.title}
                      </h3>
                      <span className="text-[10px] text-neutral-400 font-medium whitespace-nowrap">
                        {notification.createdAt?.toDate ? formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true }) : 'Just now'}
                      </span>
                    </div>
                    <p className={`text-sm leading-relaxed ${
                      !notification.read ? 'text-neutral-800 dark:text-neutral-200 font-medium' : 'text-neutral-500 dark:text-neutral-400 font-light'
                    }`}>
                      {notification.message}
                    </p>
                    {notification.type === 'pickup_reminder' && notification.data?.timeStr && (
                      <div className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-400 inline-flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                        <Clock className="w-3.5 h-3.5" />
                        Scheduled for {notification.data.timeStr}
                      </div>
                    )}
                  </div>
                  {!notification.read && (
                    <div className="mt-1">
                      <div className="w-2 h-2 rounded-full bg-indigo-600 dark:bg-indigo-400 shadow-[0_0_8px_rgba(79,70,229,0.5)]"></div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">No notifications yet</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-xs mx-auto">
              We'll notify you here when you receive new orders, pickup schedules, or important updates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
